import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Table } from "react-bootstrap";
import {
    useAdminExportAnalyserLogsMutation,
    useAdminGetAllProcessorsQuery,
    useGetTaskQuery,
    useLazyGetStoredFileQuery,
} from "../../../../store/api/kinesense";
import { StoredFile } from "../../../../models/media/StoredFile";
import { formatProcessorDate } from "./ProcessorListItem";
import ConditionalSpinner from "../../../../components/ConditionalSpinner";
import RefreshButton from "../../../../components/RefreshButton";
import { getTaskProgress } from "../../../../models/tasks/Task";
import { tryExtractErrorMessage } from "../../../../utilities/helpers";
import { Notifications } from "../../../../utilities/Notifications/Notifications";

export const ProcessorDataKeyMap: { [key: string]: string } = {
    createdAt: "Created At",
    lastStartedAt: "Last Started",
    processorId: "Processor ID",
    type: "Type",
    instanceId: "Instance ID",
    ec2InstanceType: "EC2 Instance Type",
    launcherVersion: "Launcher Version",
    isEnabled: "Enabled",
    isInitialised: "Initialised",
    supportedAnalysers: "Supported Analysers",
};

export function formatKey(key: string): string {
    return ProcessorDataKeyMap[key] ?? key;
}

export function formatValue(key: string, value: unknown): string {
    if (Array.isArray(value)) {
        return value.join(", ");
    }

    switch (key) {
        case "lastStartedAt":
            return formatProcessorDate(value as number);
        case "isEnabled":
        case "isInitialised":
            return formatBoolean(value as boolean);
        default:
            return value as string;
    }
}

export function formatBoolean(value: boolean) {
    return value ? "Yes" : "No";
}

function getDownloadFileName(storedFile: StoredFile, fallbackProcessorId?: string) {
    const keyName = storedFile.key?.split("/").pop();
    if (keyName) {
        return keyName;
    }

    if (fallbackProcessorId) {
        return `processor-${fallbackProcessorId}-logs`;
    }

    return "processor-logs";
}

function ProcessorDetails() {
    const { processorId } = useParams();
    const { data: processors, isFetching, refetch } = useAdminGetAllProcessorsQuery();
    const [requestExportAnalyserLogs, exportAnalyserLogsResult] = useAdminExportAnalyserLogsMutation();
    const [requestStoredFile] = useLazyGetStoredFileQuery();
    const [activeExportTask, setActiveExportTask] = useState<{
        projectId: string;
        taskId: string;
        outputFileId?: string;
    }>();
    const [isDownloadingExport, setIsDownloadingExport] = useState(false);
    const completedNotificationTaskRef = useRef<string>();
    const missingFileNotificationTaskRef = useRef<string>();

    const { data: activeTask } = useGetTaskQuery(
        { projectId: activeExportTask?.projectId ?? "", taskId: activeExportTask?.taskId ?? "" },
        {
            skip: !activeExportTask,
            pollingInterval: 2500,
        },
    );

    const data = processors?.find((processor) => processor.processorId == processorId);

    let flatData = data;
    if (data !== undefined) {
        flatData = { ...data, ...data.metadata };
        delete flatData.metadata;
    }

    const isActiveTaskFinished = !activeExportTask || activeTask?.isComplete || activeTask?.isFailed;

    useEffect(() => {
        if (!activeExportTask?.taskId || !activeTask?.isComplete) {
            return;
        }

        if (completedNotificationTaskRef.current === activeExportTask.taskId) {
            return;
        }

        completedNotificationTaskRef.current = activeExportTask.taskId;
        Notifications.notify(
            "Export Complete",
            `Log export has completed for processor ${data?.processorId ?? processorId}`,
            "info",
        );
    }, [activeTask?.isComplete, activeExportTask?.taskId, data?.processorId, processorId]);

    useEffect(() => {
        if (!activeExportTask?.taskId || !activeTask?.isComplete || activeExportTask.outputFileId) {
            return;
        }

        if (missingFileNotificationTaskRef.current === activeExportTask.taskId) {
            return;
        }

        missingFileNotificationTaskRef.current = activeExportTask.taskId;
        Notifications.notify(
            "Export file unavailable",
            "The export completed but no output file was returned.",
            "important",
        );
    }, [activeTask?.isComplete, activeExportTask?.outputFileId, activeExportTask?.taskId]);

    function getExportButtonText() {
        if (exportAnalyserLogsResult.isLoading) {
            return "Requesting Export...";
        }

        if (isDownloadingExport) {
            return "Downloading Logs...";
        }

        if (!activeExportTask) {
            return "Export Logs";
        }

        if (activeTask?.isComplete && activeExportTask.outputFileId) {
            return "Download Logs";
        }

        if (activeTask?.isComplete && !activeExportTask.outputFileId) {
            return "Export Complete";
        }

        if (activeTask?.isFailed) {
            return "Export Failed";
        }

        if (activeTask) {
            return `Exporting Logs... ${getTaskProgress(activeTask)}%`;
        }

        return "Exporting Logs...";
    }

    async function downloadExportedLogs() {
        if (!activeExportTask?.outputFileId) {
            Notifications.notify(
                "Export file unavailable",
                "The export completed but no output file was returned.",
                "important",
            );
            return;
        }

        setIsDownloadingExport(true);

        try {
            const storedFile = await requestStoredFile({ fileId: activeExportTask.outputFileId }).unwrap();
            const downloadUrl = storedFile.accessUrl?.url;

            if (!downloadUrl) {
                Notifications.notify(
                    "Export file unavailable",
                    "Could not find a download URL for this export.",
                    "important",
                );
                return;
            }

            const response = await fetch(downloadUrl);
            if (!response.ok) {
                throw new Error(`Failed to download file (status ${response.status})`);
            }

            const objectUrl = URL.createObjectURL(await response.blob());
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = getDownloadFileName(storedFile, data?.processorId ?? processorId);

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
        } catch (e) {
            Notifications.notify(
                "Error downloading analyser logs",
                `The following error was encountered: ${tryExtractErrorMessage(e)}`,
                "important",
            );
        } finally {
            setIsDownloadingExport(false);
        }
    }

    function exportAnalyserLogs() {
        if (!data?.processorId) {
            return;
        }

        requestExportAnalyserLogs({ processorId: data.processorId, payload: { analyserId: "ksstd" } })
            .unwrap()
            .then((result) => {
                setActiveExportTask({
                    projectId: result.projectId,
                    taskId: result.taskId,
                    outputFileId: result.outputFileId,
                });
                Notifications.notify(
                    "Export Requested",
                    `Requested analyser logs export for processor ${data.processorId}`,
                    "info",
                );
            })
            .catch((e) => {
                Notifications.notify(
                    "Error exporting analyser logs",
                    `The following error was encountered: ${tryExtractErrorMessage(e)}`,
                    "important",
                );
            });
    }

    function handleExportButtonClick() {
        if (activeTask?.isComplete && activeExportTask?.outputFileId) {
            void downloadExportedLogs();
            return;
        }

        exportAnalyserLogs();
    }

    return (
        <main>
            <section className="d-flex align-items-center justify-content-between">
                <h4 className="mb-3 fs-4">Processor Details</h4>
                <div className="d-flex align-items-center gap-2">
                    <Button
                        variant="outline-primary"
                        onClick={handleExportButtonClick}
                        disabled={
                            isFetching ||
                            exportAnalyserLogsResult.isLoading ||
                            isDownloadingExport ||
                            !data?.processorId ||
                            !isActiveTaskFinished
                        }
                    >
                        {getExportButtonText()}
                    </Button>
                    <RefreshButton callback={refetch} isDisabled={isFetching || exportAnalyserLogsResult.isLoading} />
                </div>
            </section>

            <ConditionalSpinner isLoading={isFetching}>
                <Table hover>
                    <tbody>
                        {Object.entries(flatData ?? {}).map(([key, value]) => {
                            return (
                                <tr key={key} className="text-secondary">
                                    <td style={{ width: "0px", whiteSpace: "nowrap" }} className="fw-bold pe-5">
                                        {formatKey(key)}
                                    </td>
                                    <td>{formatValue(key, value)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </ConditionalSpinner>
        </main>
    );
}

export default ProcessorDetails;
